/**
 * SSR-safe serialization of a Lexical EditorState (or its serialized
 * JSON shape) to deterministic HTML.
 *
 * Constraints (Spike S4):
 * - No DOM access (no `window`, no `document`).
 * - No `@lexical/react` imports.
 * - Runs identically in Node and workerd.
 * - Deterministic output: same input -> same bytes out.
 *
 * Supported node types (T3.1 — full coverage of the editor's built-in
 * + custom nodes):
 *   root, paragraph, heading (h1-h6), text (format bitmask:
 *   bold/italic/underline/strikethrough/code), link, autolink,
 *   linebreak, list (ul/ol/check), listitem (with check state),
 *   quote (blockquote), horizontalrule (hr), code (pre>code,
 *   syntax-highlight tokens), code-highlight (span class=token),
 *   hashtag (span class=hashtag), emoji (span class={node.className}),
 *   table + tablerow + tablecell (with header bitmask), tweet
 *   (blockquote.twitter-tweet), youtube (iframe), autocomplete
 *   (skipped — decorator-only, no published output).
 *
 * Anything else falls back to walking its `children` (if present) or
 * emits nothing.
 */
// VENDORED, verbatim, from @fredericrous/lexical-multi@0.2.0
// (duro-lexical-multi, commit 6f02e05, branch spike/s4-serialize-to-html).
// The upstream type-only `import { ... } from 'lexical'` is replaced
// here with local structural stubs so this file carries no runtime OR
// type dependency on `lexical`.
//
// TEMPORARY: this copy exists only because serializeToHtml shipped to
// the spike branch but was never published (the registry has 0.1.1,
// which predates it). Delete this file and depend on the published
// @fredericrous/lexical-multi@>=0.2.0 once that branch is merged to
// main and released. The render module imports `serializeToHtml` from
// here so the swap is a one-line import change.
interface SerializedLexicalNode {
  type: string;
  version?: number;
}
interface SerializedEditorState {
  root: SerializedNode;
}
interface EditorState {
  toJSON(): SerializedEditorState;
}

// Bit values copied from lexical/LexicalConstants.
// These are stable across Lexical versions (part of the on-disk JSON
// schema) so we hard-code them instead of importing the constants
// module (which is not part of the public surface).
const IS_BOLD = 1;
const IS_ITALIC = 1 << 1;
const IS_STRIKETHROUGH = 1 << 2;
const IS_UNDERLINE = 1 << 3;
const IS_CODE = 1 << 4;

// Order matters for determinism. Inner-most wraps are applied first
// (i.e. the LAST entry here is the OUTERMOST tag in the output).
// Convention: bold outer, then italic, then underline, then
// strikethrough, then code (most "structural" innermost). This is
// arbitrary but deterministic.
const FORMAT_TAGS: ReadonlyArray<readonly [number, string]> = [
  [IS_CODE, "code"],
  [IS_STRIKETHROUGH, "s"],
  [IS_UNDERLINE, "u"],
  [IS_ITALIC, "em"],
  [IS_BOLD, "strong"],
];

/**
 * A minimal theme shape. Currently unused by the spike output (the
 * tests assert un-themed semantic HTML) but the parameter is in the
 * public signature so T3.1 can wire it through without a breaking
 * change.
 */
export interface SerializeTheme {
  paragraph?: string;
  heading?: Partial<Record<"h1" | "h2" | "h3" | "h4" | "h5" | "h6", string>>;
  link?: string;
  list?: {
    ul?: string;
    ol?: string;
    listitem?: string;
  };
  text?: Partial<
    Record<"bold" | "italic" | "underline" | "strikethrough" | "code", string>
  >;
}

export interface SerializeOptions {
  /** Optional theme (class names for output elements). Unused in the
   *  spike scope; reserved for T3.1. */
  theme?: SerializeTheme;
}

/**
 * Accept either:
 *  - a live `EditorState` (we call `.toJSON()` on it), or
 *  - an already-serialized `SerializedEditorState` (the JSON shape).
 *
 * We do NOT mutate the live EditorState — `toJSON()` is read-only.
 */
type StateLike = EditorState | SerializedEditorState;

interface SerializedNode extends SerializedLexicalNode {
  children?: SerializedNode[];
  // Optional fields present on various concrete node types.
  text?: string;
  format?: number | string;
  tag?: string;
  url?: string;
  rel?: string | null;
  target?: string | null;
  title?: string | null;
  listType?: "number" | "bullet" | "check";
  start?: number;
  value?: number;
  checked?: boolean | undefined;
  // CodeNode + CodeHighlightNode
  language?: string | null;
  highlightType?: string | null;
  // EmojiNode
  className?: string;
  // TableCellNode header bitmask: 0=normal, 1=row, 2=column, 3=both
  headerState?: number;
  // TableCellNode dimensions (rendered as colspan/rowspan when > 1)
  colSpan?: number;
  rowSpan?: number;
  width?: number | null;
  backgroundColor?: string | null;
  // TweetNode
  tweetID?: string;
  // YouTubeNode
  videoID?: string;
}

function escapeText(s: string): string {
  // XHTML-safe text escaping. We do NOT escape apostrophes — the W3C
  // recommends &amp; &lt; &gt; for text-content; apostrophes are safe
  // in text but we escape them anyway for paranoia in attribute
  // values (separate function below).
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c === 38)
      out += "&amp;"; // &
    else if (c === 60)
      out += "&lt;"; // <
    else if (c === 62)
      out += "&gt;"; // >
    else out += s[i];
  }
  return out;
}

function escapeAttr(s: string): string {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c === 38) out += "&amp;";
    else if (c === 60) out += "&lt;";
    else if (c === 62) out += "&gt;";
    else if (c === 34) out += "&quot;";
    else out += s[i];
  }
  return out;
}

/**
 * Render an opening tag + attributes (sorted alphabetically for
 * deterministic byte-output). Attribute values are escaped.
 * If `selfClose` is true, emits `<tag />`.
 */
function openTag(
  tag: string,
  attrs: Record<string, string | undefined | null> = {},
  selfClose = false,
): string {
  const keys = Object.keys(attrs)
    .filter((k) => attrs[k] !== undefined && attrs[k] !== null)
    .sort();
  let out = "<" + tag;
  for (const k of keys) {
    out += ` ${k}="${escapeAttr(String(attrs[k]))}"`;
  }
  out += selfClose ? " />" : ">";
  return out;
}

function closeTag(tag: string): string {
  return `</${tag}>`;
}

/**
 * Wrap an already-built inner-HTML string with the text-format
 * tags indicated by `format`. The output is deterministic and
 * order-stable (see FORMAT_TAGS).
 */
function applyTextFormat(inner: string, format: number): string {
  let out = inner;
  for (const [bit, tag] of FORMAT_TAGS) {
    if (format & bit) {
      out = `<${tag}>${out}</${tag}>`;
    }
  }
  return out;
}

// Lexical ElementNode alignment. Serialized as the element's `format`
// field — a string ("center"/"right"/"justify"/"start"/"end"/"left"/"")
// in current Lexical, or a small integer in older snapshots. This is
// SEPARATE from a TextNode's numeric format bitmask (bold/italic/…);
// alignElementStyle is only ever called on element nodes, so the two
// never collide. Default LTR ("", "left", "start", 0) → no style.
const ELEMENT_FORMAT_NUM: Record<number, string> = {
  1: "left",
  2: "center",
  3: "right",
  4: "justify",
  5: "start",
  6: "end",
};

/** Build the `style` attrs for an element node's alignment, or {} when
 *  it's the default left/start alignment. */
function alignElementStyle(
  node: SerializedNode,
): Record<string, string | undefined | null> {
  const raw = node.format;
  const align =
    typeof raw === "string"
      ? raw
      : typeof raw === "number"
        ? ELEMENT_FORMAT_NUM[raw]
        : "";
  if (
    align === "center" ||
    align === "right" ||
    align === "justify" ||
    align === "end"
  ) {
    return { style: `text-align:${align}` };
  }
  return {}; // left / start / "" / unknown → browser default
}

/**
 * Serialize a single node. Returns its HTML string.
 * Unknown node types fall back to rendering their children only.
 */
function serializeNode(node: SerializedNode): string {
  switch (node.type) {
    case "root":
      return serializeChildren(node);

    case "paragraph":
      return (
        openTag("p", alignElementStyle(node)) +
        serializeChildren(node) +
        closeTag("p")
      );

    case "heading": {
      const tag = (node.tag ?? "h1").toLowerCase();
      const validHeadings = ["h1", "h2", "h3", "h4", "h5", "h6"];
      const headingTag = validHeadings.includes(tag) ? tag : "h1";
      return (
        openTag(headingTag, alignElementStyle(node)) +
        serializeChildren(node) +
        closeTag(headingTag)
      );
    }

    case "text": {
      const raw = node.text ?? "";
      const rawFormat = node.format;
      const format =
        typeof rawFormat === "number"
          ? rawFormat
          : typeof rawFormat === "string"
            ? parseInt(rawFormat, 10) || 0
            : 0;
      return applyTextFormat(escapeText(raw), format);
    }

    case "linebreak":
      // XHTML void element: self-closing.
      return "<br />";

    case "link": {
      const attrs: Record<string, string | undefined | null> = {
        href: node.url ?? "",
      };
      if (node.rel) attrs.rel = node.rel;
      if (node.target) attrs.target = node.target;
      if (node.title) attrs.title = node.title;
      return openTag("a", attrs) + serializeChildren(node) + closeTag("a");
    }

    case "autolink": {
      const attrs: Record<string, string | undefined | null> = {
        href: node.url ?? "",
      };
      if (node.rel) attrs.rel = node.rel;
      if (node.target) attrs.target = node.target;
      if (node.title) attrs.title = node.title;
      return openTag("a", attrs) + serializeChildren(node) + closeTag("a");
    }

    case "list": {
      // listType is the source of truth (matches the on-disk shape).
      // `tag` is a derived field; we honor it if present but fall
      // back to listType.
      const isCheck = node.listType === "check";
      const listTag =
        node.tag === "ol" || node.listType === "number" ? "ol" : "ul";
      const attrs: Record<string, string | undefined | null> = {};
      if (
        listTag === "ol" &&
        typeof node.start === "number" &&
        node.start !== 1
      ) {
        attrs.start = String(node.start);
      }
      if (isCheck) {
        // Lexical's checklist uses <ul role="list"> with li children
        // carrying ARIA `aria-checked`. We emit a class for CSS hooks
        // + the role for accessibility. Output is byte-deterministic
        // because openTag sorts attributes.
        attrs.class = "editor-list-check";
        attrs.role = "list";
      }
      return (
        openTag(listTag, attrs) + serializeChildren(node) + closeTag(listTag)
      );
    }

    case "listitem": {
      // For checklist items, emit role + aria-checked so screen
      // readers + CSS selectors can target check state. The text
      // content is kept exactly as authored — no synthetic checkbox
      // glyph (the rendered site styles a CSS-only checkbox via
      // ::before based on the class).
      const isCheckable = typeof node.checked === "boolean";
      if (isCheckable) {
        const attrs: Record<string, string | undefined | null> = {
          "aria-checked": node.checked ? "true" : "false",
          class: node.checked
            ? "editor-listitem-checked"
            : "editor-listitem-unchecked",
          role: "checkbox",
          tabindex: "-1",
        };
        return openTag("li", attrs) + serializeChildren(node) + closeTag("li");
      }
      return openTag("li") + serializeChildren(node) + closeTag("li");
    }

    case "quote":
      return (
        openTag("blockquote", alignElementStyle(node)) +
        serializeChildren(node) +
        closeTag("blockquote")
      );

    case "horizontalrule":
      // XHTML void element: self-closing.
      return "<hr />";

    case "code": {
      // CodeNode → <pre><code class="language-X">...</code></pre>.
      // Children are typically code-highlight tokens (handled below)
      // or plain text nodes. The `language` field on CodeNode is the
      // source for the class hint Prism/Shiki consumers use.
      const inner = serializeChildren(node);
      const codeAttrs: Record<string, string | undefined | null> = {};
      if (node.language) {
        codeAttrs.class = `language-${node.language}`;
      }
      return (
        openTag("pre") +
        openTag("code", codeAttrs) +
        inner +
        closeTag("code") +
        closeTag("pre")
      );
    }

    case "code-highlight": {
      // Children of CodeNode carry a `highlightType` (e.g. "keyword",
      // "string", "comment"). We emit a token-class span so the
      // server-side syntax highlighter or a CSS theme can pick it up.
      const raw = node.text ?? "";
      const hl = node.highlightType;
      if (hl) {
        return (
          openTag("span", { class: `token ${hl}` }) +
          escapeText(raw) +
          closeTag("span")
        );
      }
      return escapeText(raw);
    }

    case "hashtag": {
      // Extends TextNode — text + format. Wrap in <span class="hashtag">
      // and apply text-format wrapping inside (so a bold hashtag is
      // <span class="hashtag"><strong>#foo</strong></span>).
      const raw = node.text ?? "";
      const rawFormat = node.format;
      const format =
        typeof rawFormat === "number"
          ? rawFormat
          : typeof rawFormat === "string"
            ? parseInt(rawFormat, 10) || 0
            : 0;
      return (
        openTag("span", { class: "hashtag" }) +
        applyTextFormat(escapeText(raw), format) +
        closeTag("span")
      );
    }

    case "emoji": {
      // EmojiNode extends TextNode and carries its own className
      // (e.g. 'emoji emoji-happy'). The class is part of the on-disk
      // shape so it survives serialization deterministically.
      const raw = node.text ?? "";
      const className = node.className ?? "emoji";
      const rawFormat = node.format;
      const format =
        typeof rawFormat === "number"
          ? rawFormat
          : typeof rawFormat === "string"
            ? parseInt(rawFormat, 10) || 0
            : 0;
      return (
        openTag("span", { class: className }) +
        applyTextFormat(escapeText(raw), format) +
        closeTag("span")
      );
    }

    case "table":
      // Lexical's TableNode is flat: direct children are tablerow
      // nodes. We wrap with <table><tbody> (HTML semantics) — no
      // <thead> distinction because Lexical encodes header on the
      // CELL via headerState, not via row position.
      return (
        openTag("table") +
        openTag("tbody") +
        serializeChildren(node) +
        closeTag("tbody") +
        closeTag("table")
      );

    case "tablerow":
      return openTag("tr") + serializeChildren(node) + closeTag("tr");

    case "tablecell": {
      // headerState is a bitmask:
      //   0 = body cell    → <td>
      //   1 = row-header   → <th scope="row">
      //   2 = col-header   → <th scope="col">
      //   3 = both         → <th scope="rowgroup"> (no colgroup
      //                        equivalent in HTML; rowgroup is the
      //                        closest semantic, matching Lexical's
      //                        intent of "this cell is a header in
      //                        both axes")
      const hs = typeof node.headerState === "number" ? node.headerState : 0;
      const cellTag = hs === 0 ? "td" : "th";
      const attrs: Record<string, string | undefined | null> = {};
      if (hs === 1) attrs.scope = "row";
      else if (hs === 2) attrs.scope = "col";
      else if (hs === 3) attrs.scope = "rowgroup";
      if (typeof node.colSpan === "number" && node.colSpan > 1) {
        attrs.colspan = String(node.colSpan);
      }
      if (typeof node.rowSpan === "number" && node.rowSpan > 1) {
        attrs.rowspan = String(node.rowSpan);
      }
      return (
        openTag(cellTag, attrs) + serializeChildren(node) + closeTag(cellTag)
      );
    }

    case "tweet": {
      // TweetNode is a decorator. Published HTML uses Twitter's
      // recommended <blockquote class="twitter-tweet"> embed shape —
      // a client-side script (widgets.js) would replace it with the
      // live tweet. For pure SSR we emit a graceful-degradation
      // fallback: a link to the tweet so the content is visible even
      // without JS.
      const id = node.tweetID ?? "";
      if (!id) return "";
      const url = `https://twitter.com/i/status/${id}`;
      return (
        openTag("blockquote", {
          class: "twitter-tweet",
          "data-tweet-id": id,
        }) +
        openTag("a", { href: url }) +
        escapeText(url) +
        closeTag("a") +
        closeTag("blockquote")
      );
    }

    case "youtube": {
      // YouTubeNode is a decorator. SSR emits the privacy-friendly
      // youtube-nocookie iframe (same URL the live editor uses) so
      // the embed works without JS hydration.
      const id = node.videoID ?? "";
      if (!id) return "";
      return (
        openTag("iframe", {
          allow:
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
          allowfullscreen: "",
          frameborder: "0",
          src: `https://www.youtube-nocookie.com/embed/${id}`,
          title: "YouTube video",
        }) + closeTag("iframe")
      );
    }

    case "autocomplete":
      // Suggestion overlay — editor-only, never published.
      return "";

    default:
      // Unknown node type: emit children if any. This is intentional
      // — keeps the function forward-compatible with un-handled
      // node types (e.g. hashtag, emoji, code-block, table, embeds)
      // while T3.1 fills them in.
      return serializeChildren(node);
  }
}

function serializeChildren(node: SerializedNode): string {
  const children = node.children;
  if (!children || children.length === 0) return "";
  let out = "";
  for (const child of children) {
    out += serializeNode(child as SerializedNode);
  }
  return out;
}

function toSerializedEditorState(state: StateLike): SerializedEditorState {
  // Live EditorState has a `.toJSON()` method; the already-serialized
  // shape is a plain object with a `root` key. Detect via duck-typing.
  if (typeof (state as EditorState).toJSON === "function") {
    return (state as EditorState).toJSON();
  }
  return state as SerializedEditorState;
}

/**
 * Serialize a Lexical EditorState (or its serialized JSON form) to
 * deterministic HTML. SSR-safe (no DOM, no Node-only globals).
 *
 * @param state    A live EditorState OR an already-serialized state
 *                 (the JSON shape returned by EditorState.toJSON()).
 * @param options  Optional options. Reserved for theme support in
 *                 T3.1; currently the spike emits un-themed semantic
 *                 HTML.
 */
export function serializeToHtml(
  state: StateLike,
  options?: SerializeOptions,
): string {
  // Reserved for T3.1; intentionally unused in the spike.
  void options;
  const json = toSerializedEditorState(state);
  const root = json.root as unknown as SerializedNode;
  if (!root) return "";
  return serializeChildren(root);
}
